import type {
	FullCourseLevel,
	FullCourseOffering,
	FullCourseOfferings,
} from "@repo/sanity-utils/query-types";
import {getKeyedCopyText, type KeyedCopyEntry} from "@repo/utils/keyedCopy";
import {isOfferingAvailableAtCampus} from "@/lib/activeCourseOffering";
import type {DiscoveryEventKey} from "@/app/actions/trackDiscoveryEventUtils";
import {CTA_COPY} from "../CTACopyConstants";
import {appendCampusQueryParameter, appendScheduleAnchor} from "../courseUrl";

export interface SecondaryCTAScheduleLink {
	href: string;
	label: string;
	description: string;
	slug: string;
}

const PLACEMENT_TEST_MODALITIES = new Set([
	"video-based",
	"chat-based",
	"self-paced",
]);

/**
 * Header text for the secondary CTA
 */
export function getSecondaryCTAHeader(
	scheduleLinks: SecondaryCTAScheduleLink[],
	keyedCopy: KeyedCopyEntry[] = []
) {
	// When multiple tracks are available
	if (scheduleLinks.length > 1) {
		return (
			getKeyedCopyText(keyedCopy, CTA_COPY.secondary.header.multiTrack.key) ||
			CTA_COPY.secondary.header.multiTrack.fallback
		);
	}

	// If the only link is book consultation, return a special header
	const [onlyLink] = scheduleLinks;
	if (onlyLink?.slug === "placement-test") {
		return (
			getKeyedCopyText(
				keyedCopy,
				CTA_COPY.secondary.header.placementTest.key
			) || CTA_COPY.secondary.header.placementTest.fallback
		);
	} else if (onlyLink?.slug === "book-consultation") {
		return (
			getKeyedCopyText(
				keyedCopy,
				CTA_COPY.secondary.header.bookConsultation.key
			) || CTA_COPY.secondary.header.bookConsultation.fallback
		);
	}

	// Otherwise, return the description of the track
	if (!onlyLink) {
		return "";
	}

	const headerTemplate =
		getKeyedCopyText(keyedCopy, CTA_COPY.secondary.header.oneTrack.key) ||
		CTA_COPY.secondary.header.oneTrack.fallback;
	return headerTemplate.replace("{{trackName}}", onlyLink.description);
}

/**
 * Get the data for a course schedule link
 */
function buildScheduleLink({
	candidate,
	campusIdForUrl,
	keyedCopy,
	slug,
	sequenceType,
	preferNoTrackLabel = false,
}: {
	candidate: FullCourseOffering;
	campusIdForUrl: string | null;
	keyedCopy: KeyedCopyEntry[];
	slug?: string;
	sequenceType?: NonNullable<FullCourseLevel>["sequenceType"];
	/** When true, use the no-track button label even if the offering has a track. */
	preferNoTrackLabel?: boolean;
}): SecondaryCTAScheduleLink {
	const hasTrack =
		candidate.track && candidate.track.slug.current !== "track-none";
	const linkLabel = hasTrack ? candidate.track.title : candidate.title;
	const linkSlug =
		slug ?? (hasTrack ? candidate.track.slug.current : candidate._key);

	let label: string;
	let description: string;

	// Special case for versions sequence type
	if (sequenceType === "versions") {
		const versionsLabelTemplate =
			getKeyedCopyText(keyedCopy, CTA_COPY.button.versions.key) ||
			CTA_COPY.button.versions.fallback;
		label = versionsLabelTemplate.replace(
			"{{versionName}}",
			candidate.sequenceLabel ?? ""
		);
		description = candidate.sequenceLabel ?? linkLabel;
	} else {
		// Otherwise this is based on track
		const trackedLabelTemplate =
			getKeyedCopyText(keyedCopy, CTA_COPY.button.track.key) ||
			CTA_COPY.button.track.fallback;
		const untrackedLabel =
			getKeyedCopyText(keyedCopy, CTA_COPY.button.noTrack.key) ||
			CTA_COPY.button.noTrack.fallback;
		label =
			hasTrack && !preferNoTrackLabel
				? trackedLabelTemplate.replace("{{trackName}}", linkLabel)
				: untrackedLabel;
		description = linkLabel;
	}

	let href = appendCampusQueryParameter(candidate.courseUrl, campusIdForUrl);
	// Versions sequence links stay on the version page without a schedule anchor.
	if (sequenceType !== "versions") {
		href = appendScheduleAnchor(href, candidate.modality.slug.current);
	}

	return {
		href,
		label,
		description,
		slug: linkSlug,
	};
}

/**
 * Builds one schedule link per course track for the active offering.
 *
 * Offerings are limited to the same modality and sequence part as the active
 * offering so, for example, selecting Part 1 only links to Part 1 schedules.
 * The active offering is included. A Map keyed by track ID removes duplicate
 * offerings for the same track while preserving the source order. When
 * duplicates exist, the last one supplies the schedule URL.
 *
 * Offerings that list campuses are also dropped when the visitor's selected
 * campus is not one of them, so a track such as Scholars is not linked at a
 * campus that does not teach it.
 *
 * Versions sequence types skip filtering and return a link for every offering.
 *
 * The selected campus is appended to each URL when one is available.
 */
export function getCourseOfferingScheduleLinks({
	courseOfferings,
	offering,
	campusIdForUrl,
	selectedCampusId = null,
	keyedCopy = [],
	sequenceType,
}: {
	courseOfferings: FullCourseOfferings;
	offering: FullCourseOffering;
	campusIdForUrl: string | null;
	selectedCampusId?: string | null;
	keyedCopy?: KeyedCopyEntry[];
	sequenceType?: NonNullable<FullCourseLevel>["sequenceType"];
}): SecondaryCTAScheduleLink[] {
	// Special case for versions sequence type
	if (sequenceType === "versions") {
		return courseOfferings.map((candidate) =>
			buildScheduleLink({
				candidate,
				campusIdForUrl,
				keyedCopy,
				slug: candidate._key,
				sequenceType,
			})
		);
	}

	// Otherwise match track and modality
	const offeringsByTrack = new Map<string, FullCourseOffering>();
	for (const candidate of courseOfferings) {
		// Only include offerings that match the active selection.
		const matchesActiveSelection =
			candidate.modality._id === offering.modality._id &&
			candidate.sequenceLabel === offering.sequenceLabel;

		// Skip offerings that don't match the active selection.
		if (!matchesActiveSelection) {
			continue;
		}

		// Skip offerings that aren't taught at the selected campus.
		if (!isOfferingAvailableAtCampus(candidate, selectedCampusId)) {
			continue;
		}

		const hasTrack =
			candidate.track && candidate.track.slug.current !== "track-none";
		const trackKey = hasTrack ? candidate.track._id : candidate._key;

		// Deduplicate tracked offerings by track; trackless offerings use their key.
		offeringsByTrack.set(trackKey, candidate);
	}

	// Now we have the offerings in an array, we use that to build the links
	const uniqueOfferings = Array.from(offeringsByTrack.values());
	const preferNoTrackLabel = uniqueOfferings.length === 1;

	return uniqueOfferings.map((candidate) =>
		buildScheduleLink({
			candidate,
			campusIdForUrl,
			keyedCopy,
			preferNoTrackLabel,
		})
	);
}

/**
 * Builds secondary CTA links.
 *
 * In-person keeps schedule links for matching offerings that are taught at the
 * selected campus, including when the course uses a versions sequence. Online
 * modalities only show placement-test or book-consultation. Non-in-person
 * versions never show secondary links.
 */
export function getSecondaryCTAScheduleLinks({
	courseOfferings,
	offering,
	campusIdForUrl,
	selectedCampusId = null,
	showBookConsultationButtons = false,
	consultationUrl = "",
	hasPlacementTest = false,
	placementTestUrl = "",
	keyedCopy = [],
	sequenceType,
}: {
	courseOfferings: FullCourseOfferings;
	offering: FullCourseOffering;
	campusIdForUrl: string | null;
	selectedCampusId?: string | null;
	showBookConsultationButtons?: boolean;
	consultationUrl?: string;
	hasPlacementTest?: boolean;
	placementTestUrl?: string;
	keyedCopy?: KeyedCopyEntry[];
	sequenceType?: NonNullable<FullCourseLevel>["sequenceType"];
}): SecondaryCTAScheduleLink[] {
	const modalitySlug = offering.modality.slug.current;

	// In-person uses schedule links.
	if (modalitySlug === "in-person") {
		return getCourseOfferingScheduleLinks({
			courseOfferings,
			offering,
			campusIdForUrl,
			selectedCampusId,
			keyedCopy,
		});
	}

	// Versions has no secondary links.
	if (sequenceType === "versions") {
		return [];
	}

	// Others show placement test or book consultation buttons.
	const links: SecondaryCTAScheduleLink[] = [];
	const shouldAddPlacementTest =
		hasPlacementTest && PLACEMENT_TEST_MODALITIES.has(modalitySlug);

	// Video-based uses book consultation buttons.
	if (showBookConsultationButtons && modalitySlug === "video-based") {
		links.push({
			href: consultationUrl,
			label:
				getKeyedCopyText(keyedCopy, CTA_COPY.button.inPerson.key) ||
				CTA_COPY.button.inPerson.fallback,
			description: offering.track.title,
			slug: "book-consultation",
		});
	} else if (shouldAddPlacementTest) {
		// Placement test only when book consultation is not shown.
		links.push({
			href: placementTestUrl,
			label:
				getKeyedCopyText(keyedCopy, CTA_COPY.button.placementTest.key) ||
				CTA_COPY.button.placementTest.fallback,
			description: offering.track?.title ?? offering.title,
			slug: "placement-test",
		});
	}

	return links;
}

export type ScheduleLinkTrackingEvent = {
	event: DiscoveryEventKey;
	properties: Record<string, unknown>;
};

/**
 * Resolves which Discovery funnel event (if any) a schedule-link click should
 * fire: "book-consultation" is a consultation exit, "placement-test" is
 * out of scope here (tracked inside apps/placement itself), everything else
 * is a plain schedule-viewing exit.
 */
export function resolveScheduleLinkTrackingEvent(
	slug: string,
	courseSlug: string
): ScheduleLinkTrackingEvent | null {
	if (slug === "book-consultation") {
		return {
			event: "consultation_request_initiated",
			properties: {course_slug: courseSlug, source: "secondary_cta"},
		};
	}
	if (slug === "placement-test") {
		return null;
	}
	return {
		event: "course_schedule_requested",
		properties: {course_slug: courseSlug, offering_slug: slug},
	};
}
